import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import EmailIcon from '@mui/icons-material/Email';
import { supabase } from '../config/supabase';
import { vCardFilename, vCardFor } from '../../shared/vcard.js';

type Team = { id: string; name: string; season?: string };
type RosterRow = { team_id: string; player_id: string; is_active: boolean; roster_status: string };
type PlayerRow = { id: string; first_name: string | null; last_name: string | null; email: string | null; mobile_phone: string | null };

export function CaptainAddressBook({ teams }: { teams: Team[] }) {
  const [rosters, setRosters] = useState<RosterRow[]>([]);
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const teamIdsKey = teams.map((team) => team.id).sort().join(',');

  useEffect(() => {
    let active = true;
    if (!teamIdsKey) { setRosters([]); setPlayers([]); return; }
    void (async () => {
      const result = await supabase.from('team_rosters')
        .select('team_id, player_id, is_active, roster_status')
        .in('team_id', teamIdsKey.split(','))
        .is('deleted_at', null);
      if (!active) return;
      if (result.error) { setError(result.error.message); return; }
      const rows = (result.data || []) as RosterRow[];
      setRosters(rows);
      const playerIds = [...new Set(rows.map((row) => row.player_id))];
      if (!playerIds.length) { setPlayers([]); return; }
      const people = await supabase.from('players').select('id, first_name, last_name, email, mobile_phone')
        .in('id', playerIds).is('deleted_at', null);
      if (!active) return;
      if (people.error) { setError(people.error.message); return; }
      setPlayers((people.data || []) as PlayerRow[]);
      setError(null);
    })();
    return () => { active = false; };
  }, [teamIdsKey]);

  const displayName = (player: PlayerRow) => [player.first_name, player.last_name].filter(Boolean).join(' ') || 'Unnamed Player';
  const filtered = useMemo(() => players.filter((player) => displayName(player).toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => displayName(a).localeCompare(displayName(b))), [players, search]);
  const selected = players.find((player) => player.id === selectedId) || null;
  const currentTeams = selected ? teams.filter((team) => rosters.some((row) => row.player_id === selected.id && row.team_id === team.id && row.is_active && row.roster_status !== 'inactive')) : [];
  const previousTeams = selected ? teams.filter((team) =>
    !currentTeams.some((current) => current.id === team.id)
    && rosters.some((row) => row.player_id === selected.id && row.team_id === team.id && (!row.is_active || row.roster_status === 'inactive')),
  ) : [];
  const copy = async (value: string, label: string) => {
    try { await navigator.clipboard.writeText(value); setNotice(`${label} copied.`); }
    catch { setError(`Unable to copy ${label.toLowerCase()}.`); }
  };
  const download = (player: PlayerRow) => {
    const name = displayName(player);
    const file = new Blob([vCardFor({ name, firstName: player.first_name, lastName: player.last_name, email: player.email, phone: player.mobile_phone })], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = vCardFilename(name);
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff' }}>
      <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Address Book</Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 1 }}>{notice}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 0.9fr) minmax(0, 1.1fr)' }, gap: 1.5 }}>
        <Box sx={{ display: { xs: showMobileDetail ? 'none' : 'block', md: 'block' } }}>
          <TextField fullWidth size="small" label="Search players by name" value={search} onChange={(event) => setSearch(event.target.value)} />
          <Box sx={{ maxHeight: 270, overflowY: 'auto', mt: 1 }}>
            {filtered.map((player) => (
              <Button key={player.id} fullWidth variant={selectedId === player.id ? 'contained' : 'text'} onClick={() => { setSelectedId(player.id); setShowMobileDetail(true); }} sx={{ display: 'block', textAlign: 'left', textTransform: 'none', mb: 0.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 750 }}>{displayName(player)}</Typography>
                <Typography variant="caption">{teams.filter((team) => rosters.some((row) => row.player_id === player.id && row.team_id === team.id && row.is_active && row.roster_status !== 'inactive')).map((team) => team.name).join(', ')}</Typography>
              </Button>
            ))}
            {filtered.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No players found.</Typography>}
          </Box>
        </Box>
        <Box sx={{ display: { xs: showMobileDetail ? 'block' : 'none', md: 'block' }, minWidth: 0 }}>
          <Button size="small" onClick={() => setShowMobileDetail(false)} sx={{ display: { xs: 'inline-flex', md: 'none' }, mb: 1 }}>Back to players</Button>
          {selected ? <Stack spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{displayName(selected)}</Typography>
            <Typography variant="body2">Email: {selected.email || 'Not available'}</Typography>
            <Typography variant="body2">Phone: {selected.mobile_phone || 'Not available'}</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {selected.email && <Button size="small" startIcon={<EmailIcon />} href={`mailto:${selected.email}`}>Email</Button>}
              {selected.email && <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => void copy(selected.email!, 'Email')}>Copy Email</Button>}
              {selected.mobile_phone && <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => void copy(selected.mobile_phone!, 'Phone')}>Copy Phone</Button>}
              <Button size="small" startIcon={<ContactPageIcon />} onClick={() => download(selected)}>Save Contact</Button>
            </Stack>
            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Current Teams</Typography>
            {currentTeams.map((team) => <Typography variant="body2" key={team.id}>{team.name}</Typography>)}
            {currentTeams.length === 0 && <Typography variant="body2" color="text.secondary">None</Typography>}
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Previous Teams</Typography>
            {previousTeams.map((team) => <Typography variant="body2" key={team.id}>{team.name}{team.season ? ` — ${team.season}` : ''}</Typography>)}
            {previousTeams.length === 0 && <Typography variant="body2" color="text.secondary">None available</Typography>}
          </Stack> : <Typography variant="body2" color="text.secondary">Select a player to view contact details.</Typography>}
        </Box>
      </Box>
    </Paper>
  );
}
